import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { useChat } from './ChatContext';
import VoiceService from '../services/voiceService';

const VoiceContext = createContext(null);

/**
 * Voice state machine:
 *   idle -> connecting -> ready <-> recording -> processing -> speaking -> ready
 *   (with isFadingOut during speaking -> ready transition)
 *   any state -> idle (on disconnect/error)
 */
export function VoiceProvider({ children }) {
  const [voiceState, setVoiceState] = useState('idle'); // idle | connecting | ready | recording | processing | speaking
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [inputTranscript, setInputTranscript] = useState('');
  const [outputTranscript, setOutputTranscript] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const [voiceError, setVoiceError] = useState(null);

  const { addVoiceMessages } = useChat();
  const serviceRef = useRef(null);
  const fadeTimeoutRef = useRef(null);
  const pendingMessagesRef = useRef([]);

  /**
   * Start a voice session — connect to backend WebSocket, init audio.
   */
  const startVoice = useCallback(async (sessionId) => {
    if (!sessionId) return;

    setVoiceState('connecting');
    setIsVoiceMode(true);
    setVoiceError(null);
    setIsFadingOut(false);
    setInputTranscript('');
    setOutputTranscript('');
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }

    const service = new VoiceService();
    serviceRef.current = service;

    // Wire up callbacks
    service.onReady = () => {
      setVoiceState('ready');
    };

    service.onInputTranscription = (text) => {
      setInputTranscript((prev) => (prev ? prev + ' ' + text : text));
    };

    service.onOutputTranscription = (text) => {
      // First output transcription means AI is speaking
      setVoiceState('speaking');
      setOutputTranscript((prev) => (prev ? prev + ' ' + text : text));
    };

    // Buffer saved messages — don't show in chat until turn fully completes
    service.onMessagesSaved = (messages) => {
      pendingMessagesRef.current.push(...messages);
    };

    // Backend says turn is done generating — but audio may still be playing
    service.onTurnComplete = () => {
      setVoiceState((prev) => prev !== 'speaking' ? 'speaking' : prev);
      service.markTurnComplete();
    };

    // Audio actually finished playing — start fade-out transition
    service.onPlaybackComplete = () => {
      setIsFadingOut(true);
      fadeTimeoutRef.current = setTimeout(() => {
        fadeTimeoutRef.current = null;
        setIsFadingOut(false);
        setInputTranscript('');
        setOutputTranscript('');
        // Flush buffered messages to chat list now
        if (pendingMessagesRef.current.length > 0) {
          addVoiceMessages(pendingMessagesRef.current);
          pendingMessagesRef.current = [];
        }
        setVoiceState('ready');
      }, 400);
    };

    service.onError = (message) => {
      setVoiceError(message);
      // Don't auto-disconnect on transient errors
    };

    service.onClosed = () => {
      // Flush any buffered messages so they aren't lost
      if (pendingMessagesRef.current.length > 0) {
        addVoiceMessages(pendingMessagesRef.current);
        pendingMessagesRef.current = [];
      }
      setVoiceState('idle');
      setIsVoiceMode(false);
      setIsFadingOut(false);
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      serviceRef.current = null;
    };

    service.onAudioLevel = (level) => {
      setAudioLevel(level);
    };

    try {
      await service.connect(sessionId);
    } catch (err) {
      setVoiceState('idle');
      setIsVoiceMode(false);
      serviceRef.current = null;
    }
  }, [addVoiceMessages]);

  /**
   * Stop voice session — disconnect everything, return to text mode.
   */
  const stopVoice = useCallback(() => {
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
    if (serviceRef.current) {
      serviceRef.current.disconnect();
      serviceRef.current = null;
    }
    // Flush any buffered messages so they aren't lost
    if (pendingMessagesRef.current.length > 0) {
      addVoiceMessages(pendingMessagesRef.current);
      pendingMessagesRef.current = [];
    }
    setVoiceState('idle');
    setIsVoiceMode(false);
    setIsFadingOut(false);
    setAudioLevel(0);
    setInputTranscript('');
    setOutputTranscript('');
    setVoiceError(null);
  }, [addVoiceMessages]);

  /**
   * Push-to-talk press — start streaming mic audio.
   */
  const startRecording = useCallback(() => {
    const canRecord = voiceState === 'ready' || voiceState === 'speaking';
    if (serviceRef.current && canRecord) {
      // Cancel any pending fade-out if user is fast
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      // Flush any buffered messages from the interrupted turn
      if (pendingMessagesRef.current.length > 0) {
        addVoiceMessages(pendingMessagesRef.current);
        pendingMessagesRef.current = [];
      }
      setIsFadingOut(false);
      setVoiceState('recording');
      setInputTranscript('');
      setOutputTranscript('');
      serviceRef.current.startRecording();
    }
  }, [voiceState, addVoiceMessages]);

  /**
   * Push-to-talk release — signal end of speech.
   */
  const stopRecording = useCallback(() => {
    if (serviceRef.current && voiceState === 'recording') {
      setVoiceState('processing');
      serviceRef.current.stopRecording();
    }
  }, [voiceState]);

  const value = {
    voiceState,
    isVoiceMode,
    isFadingOut,
    inputTranscript,
    outputTranscript,
    audioLevel,
    voiceError,
    startVoice,
    stopVoice,
    startRecording,
    stopRecording,
  };

  return (
    <VoiceContext.Provider value={value}>
      {children}
    </VoiceContext.Provider>
  );
}

export function useVoice() {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
}

export default VoiceContext;
